/**
 * Inline SVG icons. Original artwork, drawn for this widget. All are
 * decorative (aria-hidden); every control carries its own text label.
 */

const svg = (size: number, body: string, strokeWidth = 1.8): string =>
  `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" stroke-width="${strokeWidth}" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;

const FIGURE =
  '<circle cx="12" cy="12" r="10.2"/>' +
  '<circle cx="12" cy="6.6" r="1.5" fill="currentColor" stroke="none"/>' +
  '<path d="M6.8 9.6c1.7.5 3.4.8 5.2.8s3.5-.3 5.2-.8"/>' +
  '<path d="M12 10.4v3.6"/>' +
  '<path d="M9.4 18.2 12 14l2.6 4.2"/>';

/** A person with arms out inside a ring: the launcher. */
export const LAUNCHER_ICON = svg(30, FIGURE);

/** The same figure, smaller, for the panel header badge. */
export const HEADER_ICON = svg(22, FIGURE);

export const CLOSE_ICON = svg(20, '<path d="M6 6l12 12M18 6 6 18"/>', 2.2);

export const RESET_ICON = svg(
  18,
  '<path d="M4 12a8 8 0 1 0 2.4-5.7"/><path d="M4 4v4.5h4.5"/>',
  2,
);

export const STATEMENT_ICON = svg(
  18,
  '<path d="M7 3.5h7l4 4V20a.5.5 0 0 1-.5.5h-10A.5.5 0 0 1 7 20z"/><path d="M13.5 3.5V8H18"/><path d="M10 12.5h5M10 16h5"/>',
  1.9,
);

/** Shown on a tile while its setting is on. */
export const CHECK_ICON = svg(12, '<path d="m5 12.5 4.5 4.5L19 7.5"/>', 3.2);

/** A speaker with sound waves: Read aloud. */
const SPEAKER =
  '<path d="M4 9.5h3.2L12 5.5v13l-4.8-4H4z"/><path d="M15.5 9.2a4 4 0 0 1 0 5.6M18.2 6.6a7.6 7.6 0 0 1 0 10.8"/>';
export const SPEAKER_ICON = svg(20, SPEAKER);

/** Read aloud player buttons. */
export const PLAY_ICON = svg(
  18,
  '<path d="M8 5.5v13l10-6.5z" fill="currentColor"/>',
  1.6,
);
export const PAUSE_ICON = svg(18, '<path d="M8.5 5.5v13M15.5 5.5v13"/>', 3);
export const STOP_ICON = svg(
  18,
  '<rect x="6.5" y="6.5" width="11" height="11" rx="1.5" fill="currentColor"/>',
  1.6,
);

/** One icon per on/off setting, keyed like the prefs. */
export const SETTING_ICONS: Record<string, string> = {
  speech: svg(24, SPEAKER),
  // Text lines with arrows pushing them apart.
  spacing: svg(
    24,
    '<path d="M8 7h8M8 12h8M8 17h8"/><path d="M4 9.5 2.5 12 4 14.5M20 9.5l1.5 2.5-1.5 2.5"/>',
  ),
  // Two chain links.
  links: svg(
    24,
    '<path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7L11.5 6.8"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1.5-1.5"/>',
  ),
  // A highlighted band across lines of text.
  guide: svg(
    24,
    '<path d="M4 5h16M4 19h16"/><rect x="2.5" y="9" width="19" height="6" rx="1.5"/><path d="M6 12h12"/>',
  ),
  // A large arrow pointer.
  cursor: svg(
    24,
    '<path d="M5 3.5 19 10l-6.2 1.8L10.5 18z"/><path d="m13 12 5 5"/>',
  ),
  // Pause bars in a circle.
  motion: svg(
    24,
    '<circle cx="12" cy="12" r="8.5"/><path d="M10 9v6M14 9v6"/>',
    2,
  ),
  // A picture with a line through it.
  images: svg(
    24,
    '<rect x="3.5" y="5" width="17" height="14" rx="2"/><path d="m3.5 16 4.5-4.5 3.5 3.5 2.5-2.5 3.5 3.5"/><path d="M3 3l18 18"/>',
  ),
};

/** Text alignment glyphs: lines flush left (also shown while off), centered, flush right. */
export const ALIGN_ICONS: Record<string, string> = {
  left: svg(18, '<path d="M4 6h16M4 10h10M4 14h16M4 18h10"/>', 2),
  center: svg(18, '<path d="M4 6h16M7 10h10M4 14h16M7 18h10"/>', 2),
  right: svg(18, '<path d="M4 6h16M10 10h10M4 14h16M10 18h10"/>', 2),
};

/** Text size: a small T and a big T. */
export const TEXT_SIZE_ICON = svg(
  28,
  '<path d="M2.5 10h8M6.5 10v9.5M11.5 5h10M16.5 5v14.5"/>',
  2,
);

/** Saturation glyphs: an outlined drop, half filled, filled, and dotted. */
const DROP =
  '<path d="M12 3.2c3.3 4.2 5.8 7.4 5.8 10.6a5.8 5.8 0 0 1-11.6 0c0-3.2 2.5-6.4 5.8-10.6z"/>';
export const SATURATION_ICONS: Record<string, string> = {
  none: svg(28, DROP),
  low: svg(
    28,
    DROP +
      '<path d="M12 3.2c3.3 4.2 5.8 7.4 5.8 10.6a5.8 5.8 0 0 1-5.8 5.8z" fill="currentColor" stroke="none"/>',
  ),
  high: svg(
    28,
    '<path d="M12 3.2c3.3 4.2 5.8 7.4 5.8 10.6a5.8 5.8 0 0 1-11.6 0c0-3.2 2.5-6.4 5.8-10.6z" fill="currentColor"/>',
  ),
  gray: svg(
    28,
    DROP +
      '<g fill="currentColor" stroke="none"><circle cx="13.6" cy="9.6" r=".9"/><circle cx="15.6" cy="12.6" r=".9"/><circle cx="13.2" cy="13.4" r=".9"/><circle cx="15.4" cy="15.8" r=".9"/><circle cx="13" cy="17" r=".9"/><circle cx="10.6" cy="15.8" r=".9"/></g>',
  ),
};

/** Contrast glyphs: page as-is, dark page, light page, inverted screen. */
export const CONTRAST_ICONS: Record<string, string> = {
  none: svg(
    18,
    '<circle cx="12" cy="12" r="8"/><path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" stroke="none"/>',
  ),
  dark: svg(
    18,
    '<path d="M19 14.5A7.5 7.5 0 0 1 9.5 5a7.5 7.5 0 1 0 9.5 9.5z" fill="currentColor"/>',
  ),
  light: svg(
    18,
    '<circle cx="12" cy="12" r="3.8"/><path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.6 1.6M17.1 17.1l1.6 1.6M5.3 18.7l1.6-1.6M17.1 6.9l1.6-1.6"/>',
  ),
  invert: svg(
    18,
    '<rect x="3" y="4" width="18" height="12.5" rx="1.6"/><path d="M3.6 15.9 20.4 4.6v11.3z" fill="currentColor" stroke="none"/><path d="M12 16.5v3.5M8 20h8"/>',
  ),
};
